import { IMembersSearchResultsItem } from 'Frontend/scripts/models/members.model'
import { getCurrentUser, getModeratorsUsers } from 'Frontend/scripts/services/users.service'
import { getForumThreads, assignUserToThread, unassignUserToThread } from 'Frontend/scripts/services/forums.service'
import { IForumSearchResults } from 'Frontend/scripts/models/forums.model'
import { createContext, useContext, useState, ReactNode, useEffect, useMemo } from 'react'
import { TypeOfThreads } from '../CommunityDashboard'
import { IUser } from 'Frontend/scripts/models/user.model'

interface ManageThreadsContextType {
  assignableUsers: IMembersSearchResultsItem[]
  myThreads: IForumSearchResults | null
  unassignedThreads: IForumSearchResults | null
  allThreads: IForumSearchResults | null
  isLoading: boolean

  assignThreadToUser: (userId: number, threadId: number) => void
  unassignThreadFromUser: (threadId: number) => void
  getThreads: (pageIndex: number, type: TypeOfThreads) => void
}

const ManageThreadsContext = createContext<ManageThreadsContextType | undefined>(undefined)

export const ManageThreadsProvider = ({ children }: { children: ReactNode }) => {
  const [assignableUsers, setAssignableUsers] = useState<IMembersSearchResultsItem[]>([])
  const [allThreads, setAllThreads] = useState<IForumSearchResults | null>(null)
  const [myThreads, setMyThreads] = useState<IForumSearchResults | null>(null)
  const [unassignedThreads, setUnassignedThreads] = useState<IForumSearchResults | null>(null)
  const [user, setUser] = useState<IUser>()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Fetches all assignable users
  const getAllAssignableUsers = () => {
    getModeratorsUsers(true).then((assignableUsers) => {
      setAssignableUsers(assignableUsers)
    })
  }

  const assignThreadToUser = async (userId: number, threadId: number) => {
    setIsLoading(true)

    await assignUserToThread(userId, threadId).then((result) => {
      if (result) {
        getMyThreads()
        getAllUnassignedThreads()
        getAllThreads()
      }
    })
    setIsLoading(false)
  }

  const unassignThreadFromUser = async (threadId: number) => {
    setIsLoading(true)

    await unassignUserToThread(threadId).then((result) => {
      if (result) {
        getMyThreads()
        getAllUnassignedThreads()
        getAllThreads()
      }
    })
    setIsLoading(false)
  }

  // Get threads with paging and type
  const getThreads = (pageIndex: number, type: TypeOfThreads) => {
    getForumThreads(
      pageIndex,
      type == TypeOfThreads.UnassignedThreads ? true : false,
      type == TypeOfThreads.MyThreads ? user?.id : undefined,
    ).then((threads) => {
      if (type == TypeOfThreads.UnassignedThreads) setUnassignedThreads(threads)
      else if (type == TypeOfThreads.MyThreads) setMyThreads(threads)
      else {
        setAllThreads(threads)
      }
    })
  }

  const getAllUnassignedThreads = () => {
    getForumThreads(1, true, undefined).then((threads) => {
      setUnassignedThreads(threads)
    })
  }

  const getAllThreads = () => {
    getForumThreads(1, false, undefined).then((threads) => {
      setAllThreads(threads)
    })
  }

  const getMyThreads = () => {
    getForumThreads(1, false, user?.id).then((threads) => {
      setMyThreads(threads)
    })
  }

  useMemo(() => {
    if (!user) return

    getForumThreads(1, false, user?.id).then((threads) => {
      setMyThreads(threads)
    })
  }, [user])

  useEffect(() => {
    getCurrentUser().then((user) => {
      setUser(user)
    })
    getAllAssignableUsers()
    getAllUnassignedThreads()
    getAllThreads()
  }, [])

  return (
    <ManageThreadsContext.Provider
      value={{
        assignableUsers,
        myThreads,
        unassignedThreads,
        allThreads,
        isLoading,
        assignThreadToUser,
        unassignThreadFromUser,
        getThreads,
      }}
    >
      {children}
    </ManageThreadsContext.Provider>
  )
}

export const useManageThreadsContext = () => {
  const context = useContext(ManageThreadsContext)
  if (!context) {
    throw new Error('useManageThreads must be used within a ManageThreadsProvider')
  }
  return context
}
